// --- Dependencies -------------------------------------------------------------------------------
import { FormControl, InputLabel, MenuItem, Select, type SelectChangeEvent } from "@mui/material";
import { useId } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import "../field.css";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    value: string;
    onChange: (value: string) => void;
    options: { value: string; label: string }[];
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    /** The bar's variant — shorter, to sit level with the buttons beside it. */
    compact?: boolean;
};

/**
 * MUI's select, wearing our tokens — see `field.css`. A native one cannot be styled to match.
 *
 * It floats its label like the text fields do, so a form reads the same all the way down — and so
 * does a filter bar, which `compact` only makes shorter.
 */
export default function SelectInput({ value, onChange, options, label, placeholder, disabled, compact }: Props) {
    const id = useId();

    return (
        <FormControl className={`field select-input${compact ? " field--compact" : ""}`} fullWidth={!compact} disabled={disabled}>
            {label && (
                <InputLabel id={id} shrink={!!value}>
                    {label}
                </InputLabel>
            )}
            <Select
                labelId={label ? id : undefined}
                // Without the label text the notch is cut to the wrong width and the border runs
                // straight through the word.
                label={label}
                notched={label ? !!value : undefined}
                value={value}
                onChange={(event: SelectChangeEvent) => onChange(event.target.value)}
                displayEmpty
                renderValue={(selected) => {
                    if (!selected) return <span className="select-input__placeholder">{placeholder ?? ""}</span>;
                    return options.find((option) => option.value === selected)?.label ?? selected;
                }}
                MenuProps={{ slotProps: { paper: { className: "select-input__paper" } } }}
            >
                {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
